import { useEffect, useRef, useState } from 'react';

// Somebody who has asked their machine to stop moving things has asked this
// page too. Checked once at module load rather than watched: the page is one
// screenful of animation, not an app that has to respond to the setting
// changing under it.
export const REDUCED_MOTION =
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// How far up the window a block has to reach before it counts as arrived.
// Short of the bottom edge, so it is already moving as it comes into view
// rather than starting a beat after it lands.
const TRIGGER = 0.88;

/**
 * Every block still waiting to appear, and the one listener they share.
 *
 * This was an IntersectionObserver per block, which is the obvious way to do
 * it and quietly wrong: an observer only reports a *change* in intersection,
 * and a jump - an anchor link, a scrollbar dragged, a phone flicked hard -
 * moves a block from below the window to above it inside a single frame. It
 * never intersects, so no entry is ever delivered, and that block stays
 * invisible for the rest of the visit. Scrolling back up finds a blank gap.
 *
 * Asking where things are instead of waiting to be told cannot miss that,
 * because it re-reads positions rather than transitions. One listener and one
 * pass over a handful of elements, rAF-throttled so a flick costs one
 * measurement a frame however many events it fires.
 */
const pending = new Map();
let scheduled = false;

function sweep() {
  scheduled = false;
  const limit = window.innerHeight * TRIGGER;

  for (const [node, show] of pending) {
    // Anything at or above the trigger line, including everything already
    // scrolled past - which is the case an observer misses.
    if (node.getBoundingClientRect().top <= limit) {
      pending.delete(node);
      show();
    }
  }
  if (pending.size === 0) stopWatching();
}

const schedule = () => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(sweep);
};

let watching = false;

function startWatching() {
  if (watching) return;
  watching = true;
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
}

function stopWatching() {
  if (!watching) return;
  watching = false;
  window.removeEventListener('scroll', schedule);
  window.removeEventListener('resize', schedule);
}

function watch(node, show) {
  pending.set(node, show);
  startWatching();
  // Measured now as well as on the next scroll, so whatever is already on
  // screen when the page opens appears without anybody having to move.
  schedule();
  return () => {
    pending.delete(node);
    if (pending.size === 0) stopWatching();
  };
}

/**
 * A ref to attach to anything that should animate in as it is scrolled to,
 * plus whether that has happened yet.
 *
 * Nothing ever animates back out. A heading that fades away when it leaves the
 * top of the window is a heading you cannot scroll back up to read.
 */
export function useReveal() {
  const ref = useRef(null);
  // Reduced motion starts revealed, so the content is simply there.
  const [shown, setShown] = useState(REDUCED_MOTION);

  useEffect(() => {
    const node = ref.current;
    if (!node || REDUCED_MOTION || shown) return undefined;
    return watch(node, () => setShown(true));
  }, [shown]);

  return [ref, shown];
}

/**
 * The props to spread onto a block that should animate in when it is reached.
 *
 * It takes the element's own classes rather than leaving the caller to write
 * `className` themselves, because spreading these props next to a `className`
 * attribute silently replaces it - which is exactly what happened, and it
 * stripped the layout class off every section on the page.
 */
export function useRevealProps(className = '') {
  const [ref, shown] = useReveal();
  return {
    ref,
    className: [className, 'reveal', shown && 'is-shown'].filter(Boolean).join(' '),
  };
}
